import { NOTIFICATION_TYPES } from '@/constants/notifications';
import {
  FEEDBACK_ID_PARAM,
  FEEDBACK_INTERVIEW_ID_PARAM,
  HISTORY_PARAM,
  INTERVIEW_ID_PARAM,
  SHOW_DATE_PROPOSE_PARAM,
  SHOW_FEEDBACK_PARAM,
  SHOW_OFFER_PARAM,
  STAGE_NAME_PARAM,
  STAGE_PARAM,
} from '@/constants/queryParams';
import {
  CANDIDATES,
  CANDIDATES_SPECIFICATION_SETTINGS,
  HIRING_APPROVALS,
  JOBS,
  MESSAGES,
} from '@/constants/routes';

import type { CurrentUserRoleFlags } from '@/hooks/useCurrentUser';
import type { TFunction } from 'i18next';

interface NotificationPersonRef {
  id: string;
  name: string;
}

interface NotificationClientRef extends NotificationPersonRef {
  company_id?: string;
}

export interface NotificationData {
  candidate?: NotificationPersonRef;
  current_stage?: { name: string };
  interview?: { id: string };
  feedback_id?: string;
  sender?: NotificationPersonRef;
  recruiter?: NotificationPersonRef;
  client?: NotificationClientRef;
  job_title?: string;
  job_id?: string;
  company_name?: string;
  candidate_name?: string;
  invoice_reference?: string;
  invoice_id?: string;
  current_offer?: unknown;
}

export interface NotificationProps {
  created_at: string;
  data: NotificationData;
  is_clickable: boolean;
  id: string;
  notification_type: number;
  read: boolean;
}

interface ResolvedNotification {
  name: string;
  stage: string;
  link: string;
  message: string;
  disabled: boolean;
  showUnreadDot: boolean;
}

export const resolveNotification = (
  notification: NotificationProps,
  isClient: boolean,
  { isRecruiter }: Pick<CurrentUserRoleFlags, 'isRecruiter'>,
  t: TFunction,
): ResolvedNotification => {
  const search = new URLSearchParams();
  let stage = '';
  let link = '';
  let name = '';
  const { data } = notification;

  if (notification.notification_type === 2) {
    name = data.candidate?.name ?? '';
    stage = data.current_stage?.name ?? '';
    if (data.interview?.id) {
      search.set(INTERVIEW_ID_PARAM, data.interview.id);
    }
    search.set(STAGE_PARAM, stage.toLowerCase());
    search.set(SHOW_DATE_PROPOSE_PARAM, 'true');
    link += `${CANDIDATES}?filter=in-process&${search.toString()}`;
  }

  if (notification.notification_type === 3) {
    name = data.candidate?.name ?? '';
    stage = data.current_stage?.name ?? '';
    if (data.interview?.id) {
      search.set('tasks', data.interview.id);
    }
    search.delete('feedback');
    link += `${CANDIDATES}?filter=in-process&${search.toString()}`;
  }

  if (notification.notification_type === 4) {
    name = data.candidate?.name ?? '';
    stage = data.current_stage?.name ?? '';
    if (data.interview?.id) {
      search.set(INTERVIEW_ID_PARAM, data.interview.id);
    }
    search.set(STAGE_PARAM, stage.toLowerCase());
    // Agency recruiters do not submit stage feedback — open the interview, not the submit modal.
    if (!isRecruiter) {
      search.set(SHOW_FEEDBACK_PARAM, 'true');
    }
    link += `${CANDIDATES}?filter=in-process&${search.toString()}`;
  }

  if (notification.notification_type === 5) {
    name = data.candidate?.name ?? '';
    if (data.candidate?.id) {
      search.set(HISTORY_PARAM, data.candidate.id);
    }
    if (data.interview?.id) {
      search.set(INTERVIEW_ID_PARAM, data.interview.id);
    }
    if (data.feedback_id) {
      search.set(FEEDBACK_ID_PARAM, data.feedback_id);
    }
    if (data.interview?.id) {
      search.set(FEEDBACK_INTERVIEW_ID_PARAM, data.interview.id);
    }
    if (data.current_stage?.name) {
      search.set(STAGE_NAME_PARAM, data.current_stage.name);
    }
    link += `${CANDIDATES}?filter=in-process&${search.toString()}`;
  }

  if (notification.notification_type === 6) {
    name = data.candidate?.name ?? '';
    if (data.candidate?.id) {
      search.set('history', data.candidate.id);
    }
    if (data.interview?.id) {
      search.set(INTERVIEW_ID_PARAM, data.interview.id);
    }
    if (data.current_stage?.name) {
      search.set(STAGE_NAME_PARAM, data.current_stage.name);
    }
    link += `${CANDIDATES}?filter=in-process&${search.toString()}`;
  }

  if (notification.notification_type === 7) {
    name = data.sender
      ? data.sender.name
      : isClient
        ? (data.recruiter?.name ?? '')
        : (data.client?.name ?? '');

    const user_id = isClient
      ? (data.recruiter?.id ?? '')
      : data.client?.company_id
        ? data.client.company_id
        : (data.client?.id ?? '');

    if (data.interview?.id) {
      link = `${MESSAGES}/${user_id}/${data.interview.id}`;
    }
  }

  if (
    notification.notification_type === 8 ||
    notification.notification_type === 30 ||
    notification.notification_type === 31 ||
    notification.notification_type === 35
  ) {
    name = data.job_title ?? '';
    if (data.job_id) {
      link += `${JOBS}/${data.job_id}`;
    }
  }

  if (notification.notification_type === 38) {
    name = data.job_title || data.company_name || '';
    if (data.job_id) {
      link += `${JOBS}/${data.job_id}`;
    }
  }

  if (notification.notification_type === 39) {
    name = data.candidate_name || data.invoice_reference || '';
    link = data.invoice_id
      ? `${HIRING_APPROVALS}?invoice_id=${data.invoice_id}`
      : HIRING_APPROVALS;
  }

  if (notification.notification_type === 40) {
    name = data.candidate_name || data.invoice_reference || '';
    link = HIRING_APPROVALS;
  }

  if (notification.notification_type === 9) {
    name = 'HirePort';
    search.delete('feedback');
    link += `${CANDIDATES_SPECIFICATION_SETTINGS}`;
  }

  if (
    notification.notification_type === 10 ||
    notification.notification_type === 13
  ) {
    name = data.candidate?.name ?? '';
    if (data.interview?.id) {
      search.set(INTERVIEW_ID_PARAM, data.interview.id);
    }
    if (data.candidate?.id) {
      search.set(SHOW_OFFER_PARAM, data.candidate.id);
    }
    link += `${CANDIDATES}?filter=in-process&${search.toString()}`;
  }

  if (
    notification.notification_type === 11 ||
    notification.notification_type === 14
  ) {
    name = data.candidate?.name ?? '';
    if (
      notification.notification_type === 14 &&
      isClient &&
      data.interview?.id
    ) {
      search.set('tasks', data.interview.id);
    }
    const query = search.toString();
    link += query
      ? `${CANDIDATES}?filter=in-process&${query}`
      : `${CANDIDATES}?filter=in-process`;
  }

  if (
    notification.notification_type === 12 ||
    notification.notification_type === 15 ||
    notification.notification_type === 16
  ) {
    if (data.interview?.id) {
      search.set(INTERVIEW_ID_PARAM, data.interview.id);
    }
    name = data.candidate?.name ?? '';
    if (isRecruiter && data.candidate?.id) {
      search.set(HISTORY_PARAM, data.candidate.id);
    }
    search.set('rejected_feedback', '1');
    if (data.interview?.id) {
      search.set(FEEDBACK_INTERVIEW_ID_PARAM, data.interview.id);
    }
    if (data.current_stage?.name) {
      search.set(STAGE_NAME_PARAM, data.current_stage.name);
    }
    link += `${CANDIDATES}?filter=in-process&${search.toString()}`;
  }

  const { notification_type } = notification;
  const candidateName = data.candidate?.name;
  const jobTitle = data.job_title;
  const message =
    notification_type === 7
      ? candidateName && jobTitle
        ? t('NEW_MESSAGE_CANDIDATE_JOB', { candidateName, jobTitle })
        : candidateName
          ? t('NEW_MESSAGE_CANDIDATE', { candidateName })
          : t('NEW_MESSAGE')
      : notification_type === 15 || notification_type === 16
        ? data.current_offer && !isClient
          ? t('CANDIDATE_IS_REJECTED')
          : t('CANDIDATE_REJECT')
        : notification_type === 12 && isClient
          ? t('OFFER_REJECT')
          : notification_type === 38
            ? t('JOB_ACTIVITY_FEED', {
                jobTitle: data.job_title,
              })
            : notification_type === 30
              ? t('JOB_IS_UNPUBLISHED')
              : notification_type === 31
                ? t('JOB_IS_UPDATED')
                : notification_type === 35
                  ? t('JOB_IS_ONHOLD')
                  : notification_type === 39
                    ? t('INVOICE_APPROVED')
                    : notification_type === 40
                      ? t('INVOICE_DISPUTED')
                      : t(NOTIFICATION_TYPES[notification.notification_type]);

  const disabled =
    !notification.is_clickable && notification.notification_type !== 5;
  const showUnreadDot = notification.is_clickable && !notification.read;

  return { name, stage, link, message, disabled, showUnreadDot };
};
